import React, { useState, useEffect, useMemo } from "react";
import DataTable from "../components/DataTable.js";
import { fetchDataFromAPI, saveData, deleteData } from "../components/api.js";
import currencies from "../data/currencies.js";
import subscriptionStatus from "../components/SubscriptionStatus.js";
import { jwtDecode } from "jwt-decode";

const getRangeInputProps = (col) => {
  if (col.type === "range") {
    return { min: 0, max: 5, step: 0.5 }; // Limit range inputs to 0-5 with step of 0.1
  }
  return {}; // Return empty object for non-range types
};
const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({});
  const [sortField, setSortField] = useState(""); // Track the current sorted field
  const [sortOrder, setSortOrder] = useState("desc"); // Track the current sort order
  const [materialsOptions, setMaterialsOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("USD");
  const [error, setError] = useState(null);
  const isSubscribed = subscriptionStatus();

  useEffect(() => {
    initializeCurrency();
    fetchData();
  }, []);

  // 🧠 Get default currency directly from token

  const initializeCurrency = () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const decoded = jwtDecode(token);
      const tokenCurrency = decoded?.currency; // e.g. "EGP"
      // Build dropdown options
      const options = currencies.map((c) => ({
        value: c.code, // e.g. "EGP"
        label: `${c.code} `, // user-friendly label
      }));

      setCurrencyOptions(options);
      console.log("currencies", options);

      // Find currency by code from token (like "EGP")
      const matchedCurrency = currencies.find(
        (c) => c.code.toUpperCase() === tokenCurrency?.toUpperCase()
      );
      setDefaultCurrency(matchedCurrency?.code || "USD");

      console.log("Currency options:", options);
    } catch (err) {
      console.error("Error decoding token or setting currency:", err);
    }
  };
  useEffect(() => {
    fetchData();
    console.log("referching data", currentPage, filters, sortField, sortOrder);
  }, [filters, sortField, sortOrder]);

  const fetchData = async (page = 1, itemsPerPage = 10, filters) => {
    try {
      console.log("fetchData", filters, sortField, sortOrder);
      const token = localStorage.getItem("token");
      console.log("filters", filters);
      console.log("currencies", currencies);

      // Prepare query parameters

      // Dynamically construct query parameters, excluding empty sortField and sortOrder
      const filterParamsObj = {
        page,
        limit: 10,
        ...filters,
        includeDeleted: "false",
      };

      // Add sortField and sortOrder only if they are not empty
      if (sortField) {
        filterParamsObj.sortField = sortField;
      }
      if (sortOrder) {
        filterParamsObj.sortOrder = sortOrder;
      }
      console.log("Filter Params:", new URLSearchParams(filterParamsObj));

      // Create the query string
      const filterParams = new URLSearchParams(filterParamsObj).toString();

      // Fetch expenses and materials concurrently
      const { data: expenses, pagination } = await fetchDataFromAPI(
        `/api/expenses`,
        filterParams,
        token
      );

      const { data: materials } = await fetchDataFromAPI(
        `/api/materials`,
        {},
        token
      );

      // Update state
      setExpenses(expenses || []);
      setMaterialsOptions(materials || []);
      setTotalPages(pagination?.totalPages || 1);

      console.log("Expenses:", expenses);
      console.log("Materials:", materials);
    } catch (err) {
      console.error("Error fetching data:", err.message);
      setError(err.message);
    }
  };

  const handleAddRow = async () => {
    try {
      const newRow = {
        ExpenseName: "New Expense",
        ExpenseTypeID: "", // Replace with a valid type ID or keep empty if required
        AccountID: "", // Replace with a valid account ID or keep empty
        SupplierId: "", // Replace with a valid supplier ID or keep empty
        Amount: "", // Initial amount set to 0 or update with default value
        Currency: defaultCurrency, // Default currency
        ExpenseDate: "", // Format it correctly or keep empty
        PaymentStatus: "", // Default status
        PaymentDate: "", // If required, set default or leave empty
        ApprovedBy: "", // If not needed, leave empty
        Comments: "", // Leave empty or provide a default comment
      };

      await saveData("/api/expenses", newRow, "POST");
      fetchData();
    } catch (err) {
      console.error("Error adding row:", err);
    }
  };

  const handleEdit = async (id, updatedData) => {
    try {
      await saveData(`/api/expenses/${id}`, updatedData);
      fetchData();
    } catch (err) {
      console.error("Error editing expenses:", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteData(`/api/expenses/${id}`);
      fetchData();
    } catch (err) {
      console.error("Error deleting expenses:", err);
    }
  };
  const handlePageChange = (newPage) => {
    console.log("Parent - Page changed to:", newPage); // Log in parent
    setCurrentPage(newPage); // Update the current page
    fetchData(newPage, 10); // Fetch new data
  };
  const handleSort = (field, order) => {
    console.log("handleSort,field,order", field, order);
    setSortField(field); // Update the sorted field
    setSortOrder(order); // Update the sort order (asc/desc)
  };
  const handleFilterChange = (newFilters) => {
    console.log("handleFilterChange", newFilters);
    setFilters(newFilters); // Update filters in the parent state
    fetchData(currentPage, newFilters); // Refetch data with new filters
  };

  const columns = [
    { header: "Expense Name", accessor: "ExpenseName" },
    { header: "Expense Type ", accessor: "ExpenseTypeName" },
    { header: "Account name", accessor: "AccountName" },
    { header: "Supplier name", accessor: "SupplierName" },
    { header: "Amount", accessor: "Amount", type: "range" }, // type: number for amount
    {
      header: "Currency",
      accessor: "Currency",
      searchable: true,
      type: "dropdown",
      options: currencyOptions,
    }, // type: text for currency
    { header: "Expense Date", accessor: "ExpenseDate", type: "Date" }, // type: date for date fields
    {
      header: "Payment Status",
      accessor: "PaymentStatus",
      type: "dropdown",
      options: [
        { value: "Pending", label: "Pending" },
        { value: "Paid", label: "Paid" },
        { value: "Partially Paid", label: "Partially Paid" },
        { value: "Inactive", label: "inactive" },
      ],
    },
    { header: "Payment Date", accessor: "PaymentDate", type: "Date" }, // type: date for payment date
    { header: "Approved By", accessor: "ApprovedByFirstName" },
    { header: "Created By", accessor: "CreatedByFirstName" },
    { header: "Comments", accessor: "Comments" }, // type: text for comments
    {
      header: "Materials",
      accessor: "materials",
      type: "list",
      options: materialsOptions, // Assuming materialsOptions is defined elsewhere
    },
  ];

  console.log("columns", columns);
  return (
    <DataTable
      title="Expenses"
      columns={columns}
      data={expenses}
      currentPage={currentPage}
      setData={setExpenses}
      totalPages={totalPages}
      onSort={handleSort}
      sortOrder={sortOrder}
      sortField={sortField}
      onPageChange={handlePageChange}
      filters={filters}
      onFilterChange={handleFilterChange}
      onAddRow={handleAddRow}
      actions={[
        {
          label: "Edit",
          onClick: (row) => {
            const updatedData = { ...row, name: "Updated Name" };
            handleEdit(row.id, updatedData);
          },
        },
        {
          label: "Delete",
          onClick: (row) => handleDelete(row.id),
        },
      ]}
      apiEndpoint="/api/expenses"
      itemsPerPage={10}
      fetchData={fetchData}
      getRangeInputProps={getRangeInputProps}
      selectedItems={expenses.materials}
    />
  );
};

export default Expenses;
